import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface AudioState {
  currentTime: number;
  duration: number;
  isPlaying: boolean;
  isPaused: boolean;
  isEnded: boolean;
}

export interface PodcastTrackingData {
  podcastTitle: string;
  companyTicker: string;
  researchDate: string;
  companySlug: string;
}

@Injectable({
  providedIn: 'root',
})
export class AudioTrackingService {
  private audioStateSubject = new BehaviorSubject<AudioState>({
    currentTime: 0,
    duration: 0,
    isPlaying: false,
    isPaused: false,
    isEnded: false,
  });

  public audioState$: Observable<AudioState> = this.audioStateSubject.asObservable();

  private playStartTime = 0;
  private totalPlayTime = 0;

  updateAudioState(updates: Partial<AudioState>) {
    this.audioStateSubject.next({
      ...this.audioStateSubject.value,
      ...updates,
    });
  }

  onPlay(currentTime = 0) {
    this.playStartTime = currentTime;
    this.updateAudioState({
      currentTime,
      isPlaying: true,
      isPaused: false,
      isEnded: false,
    });
  }

  onPause(currentTime: number) {
    const sessionPlayTime = currentTime - this.playStartTime;
    this.totalPlayTime += sessionPlayTime;

    this.updateAudioState({
      currentTime,
      isPlaying: false,
      isPaused: true,
      isEnded: false,
    });

    return {
      sessionPlayTime: Math.round(sessionPlayTime),
      totalPlayTime: Math.round(this.totalPlayTime),
    };
  }

  onEnded(currentTime: number) {
    const sessionPlayTime = currentTime - this.playStartTime;
    this.totalPlayTime += sessionPlayTime;

    this.updateAudioState({
      currentTime,
      isPlaying: false,
      isPaused: false,
      isEnded: true,
    });

    return {
      totalPlayTime: Math.round(this.totalPlayTime),
    };
  }

  onTimeUpdate(currentTime: number, duration: number) {
    this.updateAudioState({
      currentTime,
      duration,
    });
  }

  getSessionStats(currentTime: number) {
    const sessionPlayTime = currentTime - this.playStartTime;
    return {
      sessionPlayTime: Math.round(sessionPlayTime),
      totalPlayTime: Math.round(this.totalPlayTime),
    };
  }

  reset() {
    this.playStartTime = 0;
    this.totalPlayTime = 0;
    this.updateAudioState({
      currentTime: 0,
      duration: 0,
      isPlaying: false,
      isPaused: false,
      isEnded: false,
    });
  }

  getTotalPlayTime(): number {
    return this.totalPlayTime;
  }
}
